import { MatDialogRef } from '@angular/material/dialog';
import { createAction, props } from '@ngrx/store';
import {
  Driver,
  FareCalculator,
  FeeScheduleApprovalRequestSubmission,
  IGeoCoords,
  IgnoredRideRequest,
  InterestedDriver,
  OffPlatformReservationInfo,
  ProviderDetail,
  ProviderFeeSchedule,
  ProviderFeeScheduleApprovalObjectInfo,
  ProviderFeeScheduleApprovalStatus,
  ProviderServiceLevel,
  ServiceLevel,
  Tenant,
  TransitProgram,
  User,
  ProviderDispatchFeedItem,
  ProviderDriver,
  Provider,
  ProviderDispatcher
} from '@uzurv/clara';
import { AutoCompleteResult, HttpException } from '@uzurv/core';
import { ApprovalRequestStatus } from '../../../../sdk/packages/clara/src/models/approval-request-status';
import { OnDemandRideDialogComponent } from '../../components/dialogs/on-demand-ride-dialog/on-demand-ride-dialog.component';
import { DispatchFilters, DispatchState } from './dispatch.reducer';

export const storeState = createAction('[Dispatch] Store state', props<{ state: DispatchState }>());
export const restoreState = createAction('[Dispatch] Restore state', props<{ state: DispatchState }>());

export const setProviderId = createAction('[Dispatch] Set provider ID', props<{ id: number }>());

export const resetPagination = createAction('[Dispatch] Reset pagination');

// Providers

export const getProvider = createAction('[Dispatch] Get provider', props<{ providerId: number }>());
export const getProviderSuccess = createAction('[Dispatch] Get provider success', props<{ provider: ProviderDetail }>());
export const getProviderFail = createAction('[Dispatch] Get provider failure', props<{ error: HttpException }>());

export const listProviders = createAction('[Dispatch] List providers');
export const listProvidersSuccess = createAction('[Dispatch] List providers success', props<{ providers: Array<Provider> }>());
export const listProvidersFail = createAction('[Dispatch] List providers fail', props<{ error: HttpException }>());

export const searchProviders = createAction('[Dispatch] Search providers', props<{ searchString: string }>());

export const pageProvidersList = createAction('[Dispatch] Page providers list', props<{ pageNumber: number; pageSize: number }>());

export const listProvidersForCurrentUser = createAction('[Dispatch] List providers for current user');

export const createProvider = createAction('[Dispatch] Create provider', props<{ provider: Provider }>());
export const createProviderSuccess = createAction('[Dispatch] Create provider success', props<{ provider: Provider }>());
export const createProviderFail = createAction('[Dispatch] Create provider fail', props<{ error: HttpException }>());

export const beginEditProvider = createAction('[Dispatch] Begin edit provider', props<{ providerId: number }>());
export const cancelEditProvider = createAction('[Dispatch] Cancel edit provider', props<{ providerId: number }>());

export const editProvider = createAction('[Dispatch] Edit provider', props<{ provider: Provider }>());
export const editProviderSuccess = createAction('[Dispatch] Edit provider success', props<{ providerId: number; provider: Provider }>());
export const editProviderFail = createAction('[Dispatch] Edit provider fail', props<{ error: HttpException }>());

export const listServiceLevels = createAction('[Dispatch] List service levels');
export const listServiceLevelsSuccess = createAction(
  '[Dispatch] List service levels success',
  props<{ serviceLevels: Array<ServiceLevel> }>()
);
export const listServiceLevelsFail = createAction('[Dispatch] List service levels fail', props<{ error: HttpException }>());

export const listFareCalculators = createAction('[Dispatch] List fare calculators');
export const listFareCalculatorsSuccess = createAction(
  '[Dispatch] List fare calculators success',
  props<{ fareCalculators: Array<FareCalculator> }>()
);
export const listFareCalculatorsFail = createAction('[Dispatch] List fare calculators fail', props<{ error: HttpException }>());

export const listProviderServiceLevels = createAction('[Dispatch] List provider service levels', props<{ providerId: number }>());
export const listProviderServiceLevelsSuccess = createAction(
  '[Dispatch] List provider service levels success',
  props<{ providerId: number; serviceLevels: Array<ProviderServiceLevel> }>()
);
export const listProviderServiceLevelsFail = createAction(
  '[Dispatch] List provider service levels fail',
  props<{ error: HttpException }>()
);

export const selectProviderServiceLevel = createAction(
  '[Dispatch] Select provider service level',
  props<{ transitProgramId: number; providerServiceLevel: ProviderServiceLevel }>()
);

export const selectProviderFeeScheduleApprovalStatusesForProviderServiceLevel = createAction(
  '[Dispatch] Select provider fee schedule approval statuses for provider service level',
  props<{ transitProgramId: number; providerServiceLevel: ProviderServiceLevel }>()
);

export const handleProviderFeeScheduleApprovalStatusSelected = createAction(
  '[Dispatch] Set provider fee schedule editable and non-editable values.',
  props<{ providerFeeScheduleId: number }>()
);

export const saveProviderServiceLevel = createAction(
  '[Dispatch] Save provider service level',
  props<{ providerServiceLevel: ProviderServiceLevel }>()
);
export const saveProviderServiceLevelSuccess = createAction(
  '[Dispatch] Save provider service level success',
  props<{ providerServiceLevel: ProviderServiceLevel }>()
);
export const saveProviderServiceLevelFail = createAction('[Dispatch] Save provider service level fail', props<{ error: HttpException }>());

export const listUsersByClientApproverRole = createAction('[Dispatch] List users by client approver role', props<{ userId: number }>());

export const listUsersByClientApproverRoleSuccess = createAction(
  '[Dispatch] List users by client approver role success',
  props<{ usersByClientApproverRole: Array<User> }>()
);
export const listUsersByClientApproverRoleFail = createAction(
  '[Dispatch] List users by client approver role fail',
  props<{ error: HttpException }>()
);

export const listUsersByProviderApproverRole = createAction('[Dispatch] List users by provider approver role', props<{ userId: number }>());

export const listUsersByProviderApproverRoleSuccess = createAction(
  '[Dispatch] List users by provider approver role success',
  props<{ usersByProviderApproverRole: Array<User> }>()
);
export const listUsersByProviderApproverRoleFail = createAction(
  '[Dispatch] List users by provider approver role fail',
  props<{ error: HttpException }>()
);

export const listApprovalRequestStatuses = createAction('[Dispatch] List approval request statuses');

export const listApprovalRequestStatusesSuccess = createAction(
  '[Dispatch] List approval request statuses success',
  props<{ approvalRequestStatuses: Array<ApprovalRequestStatus> }>()
);
export const listApprovalRequestStatusesFail = createAction(
  '[Dispatch] List approval request statuses fail',
  props<{ error: HttpException }>()
);

export const listProviderFeeScheduleApprovalStatuses = createAction(
  '[Dispatch] List provider fee schedule approval statuses',
  props<{ providerId: number }>()
);
export const listProviderFeeScheduleApprovalStatusesSuccess = createAction(
  '[Dispatch] List provider fee schedule approval statuses success',
  props<{ providerId: number; providerFeeScheduleApprovalStatuses: Array<ProviderFeeScheduleApprovalStatus> }>()
);
export const listProviderFeeScheduleApprovalStatusesFail = createAction(
  '[Dispatch] List provider fee schedule approval statuses fail',
  props<{ error: HttpException }>()
);

export const deleteProviderFeeSchedule = createAction(
  '[Dispatch] Delete provider fee schedule',
  props<{ providerId: number; providerFeeSchedule: ProviderFeeSchedule }>()
);
export const deleteProviderFeeScheduleSuccess = createAction(
  '[Dispatch] Delete provider fee schedule success',
  props<{ providerId: number }>()
);
export const deleteProviderFeeScheduleFail = createAction(
  '[Dispatch] Delete provider fee schedule fail',
  props<{ error: HttpException }>()
);

export const openFeeScheduleChangeRequestDialog = createAction(
  '[Dispatch] Open fee schedule change request dialog',
  props<{ transitProgramId: number; providerFeeSchedule: ProviderFeeSchedule; providerId: number }>()
);

export const setOpenPanelIndex = createAction(
  '[Dispatch] Set open panel index',
  props<{ providerId: number; providerServiceLevel: ProviderServiceLevel }>()
);

export const saveProviderFeeSchedule = createAction(
  '[Dispatch] Save provider fee schedule',
  props<{ providerId: number; feeScheduleApprovalRequestSubmission: FeeScheduleApprovalRequestSubmission; feeScheduleId: number }>()
);
export const saveProviderFeeScheduleSuccess = createAction(
  '[Dispatch] Save provider fee schedule success',
  props<{ providerId: number }>()
);
export const saveProviderFeeScheduleFail = createAction('[Dispatch] Save provider fee schedule fail', props<{ error: HttpException }>());

export const findProviderFeeScheduleInfoById = createAction(
  '[Dispatch] Find provider fee schedule info by id',
  props<{ providerId: number; providerFeeScheduleId }>()
);
export const findProviderFeeScheduleInfoByIdSuccess = createAction(
  '[Dispatch] Find provider fee schedule info by id success',
  props<{ providerFeeScheduleApprovalObjectInfo: ProviderFeeScheduleApprovalObjectInfo }>()
);
export const findProviderFeeScheduleInfoByIdFail = createAction(
  '[Dispatch] Find provider fee schedule info by id fail',
  props<{ error: HttpException }>()
);

export const resubmitProviderFeeSchedule = createAction(
  '[Dispatch] Resubmit provider fee schedule',
  props<{ feeSchedule: ProviderFeeSchedule }>()
);

export const resubmitProviderFeeScheduleSuccess = createAction(
  '[Dispatch] Resubmit provider fee schedule success',
  props<{ providerId: number; feeSchedule: ProviderFeeSchedule }>()
);

export const resubmitProviderFeeScheduleFail = createAction(
  '[Dispatch] Resubmit provider fee schedule fail',
  props<{ error: HttpException }>()
);

export const clearProviderFeeScheduleFormData = createAction(
  '[Dispatch] Clear provider fee schedule form data',
  props<{ providerId: number }>()
);

export const clearProviderDetail = createAction('[Dispatch] Clear provider detail', props<{ providerId: number }>());

export const updateApproval = createAction(
  '[Dispatch] Update approval',
  props<{ reviewNote: string; approvalRequestStatusCode: string }>()
);
export const updateApprovalSuccess = createAction('[Dispatch] Update approval success', props<{ providerId: number }>());

export const updateApprovalFail = createAction('[Dispatch] Update approval fail', props<{ error: HttpException }>());

export const selectTransitProgramForProvider = createAction(
  '[Dispatch] Select transit program for provider',
  props<{ providerId: number; transitProgramId: number }>()
);

export const addTransitProgram = createAction(
  '[Dispatch] Add transit program',
  props<{ providerId: number; transitProgram: TransitProgram }>()
);

export const selectProviderTab = createAction('[Dispatch] Select provider tab', props<{ providerId: number; tabIndex: number }>());

// Dispatchers

export const listTenants = createAction('[Dispatch] List tenants');
export const listTenantsSuccess = createAction('[Dispatch] List tenants success', props<{ tenants: Array<Tenant> }>());
export const listTenantsFail = createAction('[Dispatch] List tenants, fail', props<{ error: HttpException }>());

export const listDispatchers = createAction('[Dispatch] List dispatchers');
export const listDispatchersSuccess = createAction(
  '[Dispatch] List dispatchers success',
  props<{ dispatchers: Array<ProviderDispatcher> }>()
);
export const listDispatchersFail = createAction('[Dispatch] List dispatchers fail', props<{ error: HttpException }>());

export const pageDispatchersList = createAction('[Dispatch] Page dispatchers list', props<{ pageSize: number; pageNumber: number }>());

export const createDispatcher = createAction(
  '[Dispatch] Create dispatcher',
  props<{ providerDispatcher: ProviderDispatcher; tenantIds }>()
);
export const createDispatcherSuccess = createAction('[Dispatch] Create dispatcher success', props<{ dispatcher: ProviderDispatcher }>());
export const createDispatcherFail = createAction('[Dispatch] Create dispatcher fail', props<{ error: HttpException }>());

export const deleteDispatcher = createAction('[Dispatch] Delete dispatcher', props<{ id: number }>());
export const deleteDispatcherSuccess = createAction('[Dispatch] Delete dispatcher success', props<{ success: boolean }>());
export const deleteDispatcherFail = createAction('[Dispatch] Delete dispatcher fail', props<{ error: HttpException }>());

// Drivers

export const listDrivers = createAction('[Dispatch] List drivers');
export const listAllDrivers = createAction('[Dispatch] List all drivers');
export const listDriversSuccess = createAction('[Dispatch] List drivers success', props<{ drivers: Array<ProviderDriver> }>());
export const listDriversFail = createAction('[Dispatch] List drivers fail', props<{ error: HttpException }>());

export const pageDriversList = createAction('[Dispatch] Page drivers list', props<{ pageSize: number; pageNumber: number }>());

export const createDriver = createAction('[Dispatch] Create driver', props<{ driver: ProviderDriver }>());
export const createDriverSuccess = createAction('[Dispatch] Create driver success', props<{ driver: ProviderDriver }>());
export const createDriverFail = createAction('[Dispatch] Create driver fail', props<{ error: HttpException }>());

export const updateDriver = createAction('[Dispatch] Update driver', props<{ driver: ProviderDriver }>());
export const updateDriverSuccess = createAction('[Dispatch] Update driver success', props<{ driver: ProviderDriver }>());
export const updateDriverFail = createAction('[Dispatch] Update driver fail', props<{ error: HttpException }>());

export const deleteDriver = createAction('[Dispatch] Delete driver', props<{ id: number }>());
export const deleteDriverSuccess = createAction('[Dispatch] Delete driver success', props<{ success: boolean }>());
export const deleteDriverFail = createAction('[Dispatch] Delete driver fail', props<{ error: HttpException }>());

// Rides

export const setRidesTab = createAction('[Dispatch] Set rides tab', props<{ tab: 'Ride Requests' | 'Scheduled' }>());

export const setIncludeIgnored = createAction('[Dispatch] Set include ignored', props<{ includeIgnored: boolean }>());

export const listRides = createAction('[Dispatch] List rides');
export const listRidesSuccess = createAction('[Dispatch] List rides success', props<{ rides: Array<ProviderDispatchFeedItem> }>());
export const listRidesFail = createAction('[Dispatch] List rides fail', props<{ error: HttpException }>());

export const searchLocations = createAction('[Dispatch] Search locations', props<{ searchString: string }>());
export const searchLocationsSuccess = createAction(
  '[Dispatch] Search locations success',
  props<{ autocompleteResults: Array<AutoCompleteResult> }>()
);
export const searchLocationsFail = createAction('[Dispatch] Search locations fail', props<{ error: HttpException }>());

export const geocodeLocation = createAction('[Dispatch] Geocode location', props<{ autocompleteResult: AutoCompleteResult }>());
export const mapInitialized = createAction('[Dispatch] Map initialized');
export const setMapCenter = createAction('[Dispatch] Set map center', props<{ location: IGeoCoords }>());
export const geocodeLocationFail = createAction('[Dispatch] Geocode location fail', props<{ error: HttpException }>());

export const filterRides = createAction('[Dispatch] Filter rides', props<{ filters: DispatchFilters }>());
export const pageRides = createAction('[Dispatch] Page rides', props<{ pageNumber: number; pageSize: number }>());

export const pinRide = createAction('[Dispatch] Pin ride', props<{ ride: ProviderDispatchFeedItem }>());
export const unpinRide = createAction('[Dispatch] Unpin ride', props<{ ride: ProviderDispatchFeedItem }>());

export const deferHoverEvents = createAction('[Dispatch] Defer hover events', props<{ defer: boolean }>());

export const assignDriver = createAction('[Dispatch] Assign driver', props<{ ride: ProviderDispatchFeedItem; driver: Driver }>());
export const assignDriverSuccess = createAction('[Dispatch] Assign driver success', props<{ ride: ProviderDispatchFeedItem }>());
export const assignDriverFail = createAction('[Dispatch] Assign driver fail', props<{ error: HttpException }>());

export const ignoreRide = createAction('[Dispatch] Ignore ride', props<{ ride: ProviderDispatchFeedItem }>());
export const ignoreRideSuccess = createAction('[Dispatch] Ignore ride success', props<{ ignoredRideRequest: IgnoredRideRequest }>());
export const ignoreRideFail = createAction('[Dispatch] Ignore ride fail', props<{ error: HttpException }>());
export const unignoreRide = createAction('[Dispatch] Unignore ride', props<{ ride: ProviderDispatchFeedItem }>());
export const unignoreRideSuccess = createAction('[Dispatch] Unignore ride success');
export const unignoreRideFail = createAction('[Dispatch] Unignore ride fail', props<{ error: HttpException }>());

export const beginExpressInterest = createAction('[Dispatch] Begin express interest', props<{ ride: ProviderDispatchFeedItem }>());

export const beginEditInterest = createAction('[Dispatch] Begin edit interest', props<{ ride: ProviderDispatchFeedItem }>());

export const submitInterest = createAction('[Dispatch] Submit interest', props<{ interestedDriver: InterestedDriver }>());
export const submitInterestSuccess = createAction('[Dispatch] Submit interest success');
export const submitInterestFail = createAction('[Dispatch] Submit interest fail', props<{ error: HttpException }>());

export const cancelReservation = createAction('[Dispatch] Cancel reservation', props<{ ride: ProviderDispatchFeedItem }>());
export const cancelReservationSuccess = createAction('[Dispatch] Cancel reservation success');
export const cancelReservationFail = createAction('[Dispatch] Cancel reservation fail', props<{ error: HttpException }>());

// Focused Itineraries

export const focusItinerary = createAction('[Dispatch] Focus Itinerary', props<{ itineraryId: number }>());
export const focusItinerarySuccess = createAction(
  '[Dispatch] Focus Itinerary Success',
  props<{ rides: Array<ProviderDispatchFeedItem> }>()
);
export const focusItineraryFail = createAction('[Dispatch] Focus Itinerary fail', props<{ error: HttpException }>());
export const clearFocusedItinerary = createAction('[Dispatch] Clear Focused Itinerary');

// Data Upload

export const listOffPlatformReservations = createAction('[Dispatch] List off platform reservations');
export const listOffPlatformReservationsSuccess = createAction(
  '[Dispatch] List off platform reservations success',
  props<{ reservations: Array<OffPlatformReservationInfo> }>()
);
export const listOffPlatformReservationsFail = createAction(
  '[Dispatch] List off platform reservations fail',
  props<{ error: HttpException }>()
);

export const pageOffPlatformReservations = createAction(
  '[Dispatch] Paginate off platform reservations',
  props<{ pageNumber: number; pageSize: number }>()
);

export const submitOffPlatformReservationInfo = createAction(
  '[Dispatch] Submit off platform reservation info',
  props<{ reservations: Array<OffPlatformReservationInfo> }>()
);
export const submitOffPlatformReservationInfoSuccess = createAction(
  '[Dispatch] Submit off platform reservation info success',
  props<{ reservations: Array<OffPlatformReservationInfo> }>()
);
export const submitOffPlatformReservationInfoFail = createAction(
  '[Dispatch] Submit off platform reservation info fail',
  props<{ error: HttpException }>()
);

// On Demand Rides

export const listOnDemandRides = createAction('[Dispatch] List on demand rides');
export const listOnDemandRidesSuccess = createAction(
  '[Dispatch] List on demand rides success',
  props<{ onDemandRides: Array<ProviderDispatchFeedItem> }>()
);
export const listOnDemandRidesFail = createAction('[Dispatch] List on demand rides fail', props<{ error: HttpException }>());

export const submitInterestOnDemandRides = createAction(
  '[Dispatch] Submit interest on demand rides',
  props<{ ride: ProviderDispatchFeedItem; interestedDriver: InterestedDriver; dialogRef: MatDialogRef<OnDemandRideDialogComponent> }>()
);
export const submitInterestOnDemandRidesSuccess = createAction('[Dispatch] Submit interest on demand rides success');
export const submitInterestOnDemandRidesFail = createAction(
  '[Dispatch] Submit interest on demand rides fail',
  props<{ error: HttpException }>()
);

export const withdrawInterestOnDemandRides = createAction(
  '[Dispatch] Withdraw interest on demand rides',
  props<{
    ride: ProviderDispatchFeedItem;
    interestedDrivers: Array<InterestedDriver>;
    dialogRef: MatDialogRef<OnDemandRideDialogComponent>;
  }>()
);
